// Single-image hair coverage matting for the recolour pass.
//
// The problem a region mask can't solve: thin/wispy hair. A coarse segmentation
// mask marks a whole wispy zone as "hair", but most of those pixels are the
// BACKGROUND showing between sparse strands. Painting them (via the `color`
// blend) tints the gaps → the recoloured background glows through thin hair.
//
// The fix recovers a per-pixel hair *coverage* (opacity) matte from the photo
// itself, with no extra ML model:
//   1. Estimate the colour BEHIND the hair — inpaint the studio background /
//      skin / clothing under the whole hair region by weighted diffusion.
//   2. Coverage(p) = how far the photo pixel deviates from that backdrop.
//      gap (pixel ≈ backdrop)  → 0  → keep original, never tint.
//      strand (pixel ≠ backdrop) → 1 → full tint.
// This is background-agnostic (works over grey bg, skin at the hairline, or a
// shirt at the shoulders) and also subsumes the old halo / skin-bleed problem:
// a skin pixel that leaks into the mask matches its (skin) backdrop → coverage
// ~0 → untouched.

import { createCanvas } from './maskUtils';

function ctxOf(c: HTMLCanvasElement): CanvasRenderingContext2D {
  const ctx = c.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('2D context unavailable');
  return ctx;
}

// Separable box blur of a single Float32 plane (clamped edges), radius r.
function boxBlurPlane(
  src: Float32Array,
  w: number,
  h: number,
  r: number,
  tmp: Float32Array,
  dst: Float32Array
): void {
  const inv = 1 / (2 * r + 1);
  // horizontal pass: src -> tmp
  for (let y = 0; y < h; y++) {
    const row = y * w;
    let acc = 0;
    for (let k = -r; k <= r; k++) acc += src[row + clamp(k, 0, w - 1)]!;
    for (let x = 0; x < w; x++) {
      tmp[row + x] = acc * inv;
      acc += src[row + clamp(x + r + 1, 0, w - 1)]! - src[row + clamp(x - r, 0, w - 1)]!;
    }
  }
  // vertical pass: tmp -> dst
  for (let x = 0; x < w; x++) {
    let acc = 0;
    for (let k = -r; k <= r; k++) acc += tmp[clamp(k, 0, h - 1) * w + x]!;
    for (let y = 0; y < h; y++) {
      dst[y * w + x] = acc * inv;
      acc += tmp[clamp(y + r + 1, 0, h - 1) * w + x]! - tmp[clamp(y - r, 0, h - 1) * w + x]!;
    }
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge1 <= edge0) return x < edge0 ? 0 : 1;
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

// A bright pixel in the PHOTO at a hair edge is the shirt / skin / light
// background showing between or just beyond wispy strands, not a strand — yet
// the `color` blend would paint it an obvious magenta fringe (its luminance is
// kept, only hue+saturation change, which is glaring on a near-white shoulder).
// Fade the envelope coverage as the source pixel brightens (ramp 145→175) so
// those edge pixels over light areas stop tinting. Solid hair is darker so it's
// unaffected, and the forced `core` is exempt — the dense hairline never thins.
// Keying on the SOURCE pixel (not the inpainted backdrop, which the low-res
// diffusion drags dark near a big hair mass) is what actually catches the fringe.
export function fringeGuard(srcLuma: number): number {
  return 1 - smoothstep(145, 175, srcLuma);
}

// Estimate the colour behind the hair by diffusing surrounding pixels into the
// hair region. Runs at low resolution — the backdrop is low-frequency (smooth
// bg, skin, cloth), so a small grid is both faster and naturally smoother — and
// is upscaled back to full resolution by the caller via drawImage.
function estimateBackdrop(
  source: HTMLCanvasElement,
  holeMask: HTMLCanvasElement, // alpha>0 where hair (region to fill)
  W: number,
  H: number,
  workMax = 168,
  iterations = 48,
  blurRadius = 2
): HTMLCanvasElement {
  const scale = Math.min(1, workMax / Math.max(W, H));
  const w = Math.max(1, Math.round(W * scale));
  const h = Math.max(1, Math.round(H * scale));
  const N = w * h;

  const sCanvas = createCanvas(w, h);
  ctxOf(sCanvas).drawImage(source, 0, 0, w, h);
  const hCanvas = createCanvas(w, h);
  ctxOf(hCanvas).drawImage(holeMask, 0, 0, w, h);
  const sData = ctxOf(sCanvas).getImageData(0, 0, w, h).data;
  const hData = ctxOf(hCanvas).getImageData(0, 0, w, h).data;

  // Weighted planes: known (non-hair) pixels carry their colour at weight 1;
  // hole (hair) pixels start empty (weight 0) and get filled by diffusion.
  const known = new Uint8Array(N);
  const r0 = new Float32Array(N);
  const g0 = new Float32Array(N);
  const b0 = new Float32Array(N);
  let r = new Float32Array(N);
  let g = new Float32Array(N);
  let b = new Float32Array(N);
  let wt = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const k = (hData[i * 4 + 3]! / 255) < 0.5 ? 1 : 0;
    known[i] = k;
    r0[i] = sData[i * 4]!;
    g0[i] = sData[i * 4 + 1]!;
    b0[i] = sData[i * 4 + 2]!;
    if (k) {
      r[i] = r0[i]!;
      g[i] = g0[i]!;
      b[i] = b0[i]!;
      wt[i] = 1;
    }
  }

  let tr = new Float32Array(N);
  let tg = new Float32Array(N);
  let tb = new Float32Array(N);
  let tw = new Float32Array(N);
  const tmp = new Float32Array(N);
  for (let it = 0; it < iterations; it++) {
    boxBlurPlane(r, w, h, blurRadius, tmp, tr);
    boxBlurPlane(g, w, h, blurRadius, tmp, tg);
    boxBlurPlane(b, w, h, blurRadius, tmp, tb);
    boxBlurPlane(wt, w, h, blurRadius, tmp, tw);
    // Pin known pixels back to their exact colour so the boundary stays fixed.
    for (let i = 0; i < N; i++) {
      if (known[i]) {
        tr[i] = r0[i]!;
        tg[i] = g0[i]!;
        tb[i] = b0[i]!;
        tw[i] = 1;
      }
    }
    [r, tr] = [tr, r];
    [g, tg] = [tg, g];
    [b, tb] = [tb, b];
    [wt, tw] = [tw, wt];
  }

  // Global mean of known pixels — fallback for any hole pixel the diffusion
  // never reached (deep inside a large closed region). Without it, dividing by
  // a near-zero weight would yield a dark/garbage backdrop and spike coverage.
  let mr = 0;
  let mg = 0;
  let mb = 0;
  let mc = 0;
  for (let i = 0; i < N; i++) {
    if (known[i]) {
      mr += r0[i]!;
      mg += g0[i]!;
      mb += b0[i]!;
      mc++;
    }
  }
  const inv = mc > 0 ? 1 / mc : 0;
  mr *= inv;
  mg *= inv;
  mb *= inv;

  const out = createCanvas(w, h);
  const img = ctxOf(out).createImageData(w, h);
  for (let i = 0; i < N; i++) {
    const wsum = wt[i]!;
    if (wsum < 1e-2) {
      img.data[i * 4] = mr;
      img.data[i * 4 + 1] = mg;
      img.data[i * 4 + 2] = mb;
    } else {
      img.data[i * 4] = clamp(r[i]! / wsum, 0, 255);
      img.data[i * 4 + 1] = clamp(g[i]! / wsum, 0, 255);
      img.data[i * 4 + 2] = clamp(b[i]! / wsum, 0, 255);
    }
    img.data[i * 4 + 3] = 255;
  }
  ctxOf(out).putImageData(img, 0, 0);
  return out;
}

export interface HairMatteOptions {
  // Binary, generously-grown region to inpaint when estimating the backdrop.
  // Must cover ALL hair (incl. wisps) so the "known" pixels feeding the inpaint
  // are genuinely background, not hair. Distinct from `envelope`: a feathered
  // envelope would pin its own soft-edge wisps as "known" and zero their cover.
  hole: HTMLCanvasElement;
  // Spatial envelope (feathered): hair may exist only where this mask has alpha.
  // Coverage is computed inside it and shaped by its alpha; outside it stays 0.
  envelope: HTMLCanvasElement;
  // Definite-hair core (strongly eroded mask). Coverage is floored to 1 here so
  // solid hair is always fully recoloured regardless of backdrop estimate.
  core: HTMLCanvasElement;
  // Colour distance (0..441 RGB) below which a pixel is treated as pure backdrop
  // (coverage 0) and above which as solid strand (coverage 1). Soft ramp between.
  gapDistance?: number;
  strandDistance?: number;
}

// Build a per-pixel hair-coverage alpha mask (white canvas, alpha = coverage).
// Drop-in for the recolour layer's clip mask.
export function buildHairCoverageMatte(
  source: HTMLCanvasElement,
  W: number,
  H: number,
  opts: HairMatteOptions
): HTMLCanvasElement {
  const gap = opts.gapDistance ?? 16;
  const strand = opts.strandDistance ?? 56;

  const backdropLow = estimateBackdrop(source, opts.hole, W, H);
  // Upscale the smooth backdrop to full resolution (smoothing on by default).
  const backdrop = createCanvas(W, H);
  ctxOf(backdrop).drawImage(backdropLow, 0, 0, W, H);

  // `source` is already W×H (the photo canvas) — read it directly, no copy.
  const src = ctxOf(source).getImageData(0, 0, W, H).data;
  const bd = ctxOf(backdrop).getImageData(0, 0, W, H).data;
  const env = sampleAlpha(opts.envelope, W, H);
  const core = sampleAlpha(opts.core, W, H);

  const out = createCanvas(W, H);
  const img = ctxOf(out).createImageData(W, H);
  const N = W * H;
  for (let i = 0; i < N; i++) {
    const e = env[i]! / 255;
    if (e <= 0) {
      img.data[i * 4 + 3] = 0;
      continue;
    }
    const dr = src[i * 4]! - bd[i * 4]!;
    const dg = src[i * 4 + 1]! - bd[i * 4 + 1]!;
    const db = src[i * 4 + 2]! - bd[i * 4 + 2]!;
    // Luma-weighted distance: hair vs backdrop differences are mostly tonal.
    const dist = Math.sqrt(0.6 * dr * dr + 0.8 * dg * dg + 0.6 * db * db);
    // Fade out bright source pixels at the edge (shirt / skin / light bg showing
    // between wisps) so they don't glow a magenta fringe through the `color` blend.
    const sLuma = 0.299 * src[i * 4]! + 0.587 * src[i * 4 + 1]! + 0.114 * src[i * 4 + 2]!;
    const coverage = smoothstep(gap, strand, dist) * fringeGuard(sLuma);
    // Definite-hair core is forced solid; elsewhere coverage modulates, bounded
    // by the envelope's own feathered alpha so the outer edge still fades.
    const a = Math.max(core[i]! / 255, e * coverage);
    img.data[i * 4] = 255;
    img.data[i * 4 + 1] = 255;
    img.data[i * 4 + 2] = 255;
    img.data[i * 4 + 3] = clamp(a * 255, 0, 255);
  }
  ctxOf(out).putImageData(img, 0, 0);
  return out;
}

function sampleAlpha(mask: HTMLCanvasElement, W: number, H: number): Uint8ClampedArray {
  const c = createCanvas(W, H);
  ctxOf(c).drawImage(mask, 0, 0, W, H);
  const data = ctxOf(c).getImageData(0, 0, W, H).data;
  const out = new Uint8ClampedArray(W * H);
  for (let i = 0; i < W * H; i++) out[i] = data[i * 4 + 3]!;
  return out;
}
